import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  CheckCircle2,
  ShieldCheck,
  AlertTriangle,
  Lock,
  FileCheck,
  Upload
} from 'lucide-react';

/**
 * Fishing & Hunting Service Cards
 * Based on wireframe specifications with strict visibility rules
 */

// Card A: Fishing Guide Service (Anchor Card)
export const FishingGuideCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  requiresLicence = false,
  userHasLicence = false,
}) => {
  const canBook = !requiresLicence || userHasLicence;

  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-sky-200 bg-sky-50/20'
      } ${!canBook ? 'opacity-75' : ''}`}
      data-testid="fishing-guide-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Fishing Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🎣</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Fishing Guide</h3>
            <p className="text-xs text-gray-500">Rivers · Lakes · Coastal</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-sky-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Legal fishing spots</span>
          </p>
          <p className="text-xs text-sky-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Best times &amp; techniques</span>
          </p>
          <p className="text-xs text-sky-700 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 flex-shrink-0" />
            <span>Licensed &amp; insured guide</span>
          </p>
        </div>

        {/* Licence Required Warning */}
        {requiresLicence && !userHasLicence && (
          <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mb-2">
            <Lock className="h-3 w-3" />
            <span>Fishing licence required</span>
          </div>
        )}

        {/* Rating & Location */}
        <div className="flex items-center justify-between text-xs mb-2">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
            <span className="text-gray-500">({provider?.review_count || 96} trips)</span>
          </div>
          {provider?.travel_radius_km && (
            <span className="text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {provider.travel_radius_km} km
            </span>
          )}
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 60}</span>
        </p>

        {/* CTA */}
        <Button 
          className={`w-full h-8 text-sm ${canBook ? 'bg-sky-600 hover:bg-sky-700' : 'bg-gray-400'}`}
          onClick={() => canBook && onBook?.(service, provider)}
          disabled={!canBook}
          data-testid="book-fishing-guide"
        >
          {canBook ? 'Book guide' : 'Verify licence first'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Card B: Fishing Lessons & Casting Training
export const FishingLessonsCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-teal-200 bg-teal-50/20'
      }`}
      data-testid="fishing-lessons-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Training</span>
          </div>
        )}

        {/* Beginner Badge */}
        <Badge className="bg-teal-100 text-teal-700 text-xs mb-2">
          Beginner-friendly
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🎯</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Fishing Lessons</h3>
            <p className="text-xs text-gray-500">Casting · Lures · Catch &amp; release</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-teal-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Beginner-friendly</span>
          </p>
          <p className="text-xs text-teal-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Equipment guidance included</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.7'}</span>
          <span className="text-gray-500">({provider?.review_count || 124} lessons)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 35}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-teal-600 hover:bg-teal-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-fishing-lessons"
        >
          Book lesson
        </Button>
      </CardContent>
    </Card>
  );
};

// Card C: Equipment Setup & Repair
export const EquipmentSetupCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  trigger = 'inline', // 'inline', 'post_purchase'
}) => {
  const triggerMessages = {
    inline: null,
    post_purchase: "Your fishing gear is on the way! Need setup help?",
  };

  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-amber-200 bg-amber-50/20'
      }`}
      data-testid="equipment-setup-card"
    >
      <CardContent className="p-3">
        {/* Trigger Message */}
        {triggerMessages[trigger] && (
          <p className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1 mb-2">
            {triggerMessages[trigger]}
          </p>
        )}

        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Equipment Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🛠️</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Rod &amp; Reel Setup / Repair</h3>
            <p className="text-xs text-gray-500">Line · Drag · Guides</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Smooth performance</span>
          </p>
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Ideal for new gear</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
          <span className="text-gray-500">({provider?.review_count || 210} jobs)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 20}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-amber-600 hover:bg-amber-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-equipment-setup"
        >
          Book service
        </Button>
      </CardContent>
    </Card>
  );
};

// Card D: Guided Hunting Service (STRICT - Hidden by Default)
export const GuidedHuntingCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  countryEnabled = false,
  userAgeVerified = false,
  userLicenceApproved = false,
  providerCertified = false,
  onRequestVerification,
}) => {
  // ALL conditions must pass
  const allConditionsMet = countryEnabled && userAgeVerified && userLicenceApproved && providerCertified;
  
  // Check what's missing
  const getMissingRequirements = () => {
    const missing = [];
    if (!countryEnabled) missing.push('Not available in your region');
    if (!userAgeVerified) missing.push('Age verification required');
    if (!userLicenceApproved) missing.push('Hunting licence required');
    if (!providerCertified) missing.push('Provider certification pending');
    return missing;
  };

  const missingRequirements = getMissingRequirements();

  return (
    <Card 
      className={`overflow-hidden border-2 hover:shadow-md transition-all ${
        allConditionsMet 
          ? 'border-emerald-300 bg-emerald-50/20' 
          : 'border-red-200 bg-red-50/10'
      }`}
      data-testid="guided-hunting-card"
    >
      <CardContent className="p-3">
        {/* Sponsored / Licensed Badge */}
        {isSponsored && allConditionsMet && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Licensed Only</span>
          </div>
        )}

        {/* Warning if not all conditions met */}
        {!allConditionsMet && (
          <div className="flex items-center gap-1 text-xs text-red-600 bg-red-50 rounded px-2 py-1 mb-2">
            <AlertTriangle className="h-3 w-3" />
            <span>Restricted Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🦌</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Guided Hunting Experience</h3>
            <p className="text-xs text-gray-500">Legal · Ethical · Safe</p>
          </div>
        </div>

        {/* Verification Badges */}
        <div className="space-y-1 mb-2">
          <p className={`text-xs flex items-center gap-1 ${providerCertified ? 'text-emerald-700' : 'text-gray-400'}`}>
            <ShieldCheck className="h-3 w-3 flex-shrink-0" />
            <span>Licence verified</span>
            {providerCertified && <CheckCircle2 className="h-3 w-3 text-green-500" />}
          </p>
          <p className={`text-xs flex items-center gap-1 ${providerCertified ? 'text-emerald-700' : 'text-gray-400'}`}>
            <ShieldCheck className="h-3 w-3 flex-shrink-0" />
            <span>Insurance required</span>
            {providerCertified && <CheckCircle2 className="h-3 w-3 text-green-500" />}
          </p>
        </div>

        {/* Missing Requirements */}
        {!allConditionsMet && missingRequirements.length > 0 && (
          <div className="bg-gray-50 rounded p-2 mb-2">
            <p className="text-xs font-medium text-gray-600 mb-1">Requirements:</p>
            {missingRequirements.map((req, idx) => (
              <p key={idx} className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {req}
              </p>
            ))}
          </div>
        )}

        {/* Rating (only show if conditions met) */}
        {allConditionsMet && (
          <div className="flex items-center gap-1 text-xs mb-2">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
            <span className="text-gray-500">({provider?.review_count || 42} trips)</span>
          </div>
        )}

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 120}</span>
        </p>

        {/* CTA */}
        {allConditionsMet ? (
          <Button 
            className="w-full h-8 text-sm bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onBook?.(service, provider)}
            data-testid="book-guided-hunting"
          >
            Request booking
          </Button>
        ) : (
          <Button 
            className="w-full h-8 text-sm bg-gray-500 hover:bg-gray-600"
            onClick={onRequestVerification}
            data-testid="request-hunting-verification"
          >
            <Lock className="h-3 w-3 mr-1" />
            Verify to unlock
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Licence Verification Card
export const LicenceVerificationCard = ({
  licenceType = 'fishing', // 'fishing', 'hunting'
  status = 'none', // 'none', 'pending', 'approved', 'expired', 'rejected'
  onUpload,
  rejectionReason,
}) => {
  const statusConfig = {
    none: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', badge: null },
    pending: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', badge: 'Pending review' },
    approved: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', badge: 'Approved' },
    expired: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', badge: 'Expired' },
    rejected: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', badge: 'Rejected' },
  };

  const config = statusConfig[status];

  return (
    <Card 
      className={`${config.bg} ${config.border} border-2`}
      data-testid={`licence-verification-${licenceType}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileCheck className={`h-5 w-5 ${config.text}`} />
            <h4 className="font-medium capitalize">{licenceType} Licence Verification</h4>
          </div>
          {config.badge && (
            <Badge className={config.bg.replace('bg-', 'bg-').replace('-50', '-100') + ' ' + config.text}>
              {config.badge}
            </Badge>
          )}
        </div>

        {status === 'none' && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              Upload your {licenceType} licence to access restricted services
            </p>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => onUpload?.('licence')}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload {licenceType} licence
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => onUpload?.('id')}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload ID (age check)
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
              <Lock className="h-3 w-3" />
              <span>Encrypted storage · Used only for verification</span>
            </div>
          </>
        )}

        {status === 'pending' && (
          <p className="text-sm text-amber-600">
            Your documents are being reviewed. This usually takes 24-48 hours.
          </p>
        )}

        {status === 'approved' && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle2 className="h-5 w-5" />
            <span className="text-sm">Your licence has been verified. You can now access all services.</span>
          </div>
        )}

        {status === 'expired' && (
          <>
            <p className="text-sm text-red-600 mb-3">
              Your licence has expired. Please upload a valid licence to continue.
            </p>
            <Button 
              variant="outline" 
              className="w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => onUpload?.('licence')}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload new licence
            </Button>
          </>
        )}

        {status === 'rejected' && (
          <>
            <p className="text-sm text-red-600 mb-2">
              Your licence was rejected.
            </p>
            {rejectionReason && (
              <p className="text-xs text-red-500 bg-red-100 rounded p-2 mb-3">
                Reason: {rejectionReason}
              </p>
            )}
            <Button 
              variant="outline" 
              className="w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => onUpload?.('licence')}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload again
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Mobile Sticky Pill for Fishing & Hunting
export const FishingMobilePill = ({
  startingPrice = 20,
  currency = '£',
  onTap,
}) => {
  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 lg:hidden"
      data-testid="fishing-mobile-pill"
    >
      <button
        onClick={onTap}
        className="flex items-center gap-2 px-4 py-3 bg-sky-600 text-white rounded-full shadow-lg hover:bg-sky-700 transition-all active:scale-95"
      >
        <span>🎣</span>
        <span className="text-sm font-medium">Fishing help nearby</span>
        <span className="text-sm">From {currency}{startingPrice}</span>
      </button>
    </div>
  );
};

// Post-Checkout Upsell Card for Fishing
export const FishingPostCheckoutCard = ({
  productType,
  prompt,
  service,
  provider,
  onAdd,
  onDismiss,
}) => {
  return (
    <Card 
      className="border-2 border-sky-300 bg-gradient-to-r from-sky-50 to-white"
      data-testid="fishing-post-checkout-upsell"
    >
      <CardContent className="p-3 flex items-center gap-3">
        <span className="text-2xl">🎣</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{prompt?.title || 'Your fishing gear is on the way!'}</p>
          <p className="text-xs text-gray-500">{prompt?.subtitle || 'Want help setting it up correctly?'}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="text-xs h-8"
            onClick={onDismiss}
          >
            Not now
          </Button>
          <Button 
            size="sm" 
            className="text-xs h-8 bg-sky-600 hover:bg-sky-700"
            onClick={() => onAdd?.(service, provider)}
          >
            {prompt?.cta || 'Add service'} · {provider?.currency || '£'}{service?.price || 20}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Country Compliance Banner
export const ComplianceBanner = ({
  country,
  restrictions,
  onLearnMore,
}) => {
  if (!restrictions || Object.keys(restrictions).length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="compliance-banner">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">
            Regional Restrictions Apply
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Some services may require licences or are unavailable in {country}.
            {restrictions.huntingServices === false && ' Hunting services are not available in this region.'}
          </p>
          {onLearnMore && (
            <button 
              onClick={onLearnMore}
              className="text-xs text-amber-700 underline mt-1"
            >
              Learn more about requirements
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Fishing Bundle Card
export const FishingBundleCard = ({
  bundle,
  providers,
  currency = '£',
  onBook,
  requiresLicence = false,
  userHasLicence = false,
}) => {
  const canBook = !requiresLicence || userHasLicence;

  return (
    <Card 
      className={`overflow-hidden border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-white hover:shadow-lg transition-all ${
        !canBook ? 'opacity-75' : ''
      }`}
      data-testid={`fishing-bundle-card-${bundle.id}`}
    >
      <CardContent className="p-4">
        {/* Discount Badge */}
        <Badge className="bg-sky-600 text-white text-xs mb-2">
          Save {bundle.discount_percent}%
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-3">
          <span className="text-2xl">{bundle.icon || '🎣'}</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">{bundle.name}</h3>
            <p className="text-xs text-gray-500">{bundle.description}</p>
          </div>
        </div>

        {/* Included Services */}
        <div className="space-y-1 mb-3">
          {bundle.included_services?.map((serviceType, idx) => (
            <p key={idx} className="text-xs text-sky-700 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
              <span>{serviceType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            </p>
          ))}
        </div>

        {/* Pricing */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-gray-400 line-through text-sm">
            {currency}{bundle.original_price}
          </span>
          <span className="font-bold text-lg text-sky-700">
            {currency}{bundle.final_price}
          </span>
        </div>

        {/* CTA */}
        <Button 
          className={`w-full ${canBook ? 'bg-sky-600 hover:bg-sky-700' : 'bg-gray-400'}`}
          onClick={() => canBook && onBook?.(bundle)}
          disabled={!canBook}
          data-testid={`book-fishing-bundle-${bundle.id}`}
        >
          {canBook ? 'Get bundle' : 'Verify licence first'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default {
  FishingGuideCard,
  FishingLessonsCard,
  EquipmentSetupCard,
  GuidedHuntingCard,
  LicenceVerificationCard,
  FishingMobilePill,
  FishingPostCheckoutCard,
  ComplianceBanner,
  FishingBundleCard,
};
